import { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import { TESTIMONIALS } from '../constants';
import { useLanguage } from '../i18n/LanguageContext';

const TestimonialCard = ({ testimonial }) => {
  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6
        hover:border-[#41DB23]/50 hover:shadow-md transition-all duration-300"
      data-testid={`testimonial-${testimonial.id}`}
    >
      {/* Quote Icon */}
      <Quote className="w-8 h-8 text-[#41DB23]/30 mb-4" />

      {/* Stars */}
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 sm:w-5 sm:h-5 ${
              i < testimonial.rating
                ? 'text-[#41DB23] fill-[#41DB23]'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Text — kept in original French (authentic customer quotes) */}
      <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
        &ldquo;{testimonial.text}&rdquo;
      </p>

      {/* Author */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#41DB23]/20 rounded-full flex items-center justify-center">
          <span className="text-[#41DB23] font-bold text-sm">
            {testimonial.name.charAt(0)}
          </span>
        </div>
        <div>
          <p className="text-gray-900 font-medium text-sm">{testimonial.name}</p>
          <p className="text-gray-500 text-xs">{testimonial.city}</p>
        </div>
      </div>
    </div>
  );
};

export const Testimonials = () => {
  const { t } = useLanguage();
  const [googleData, setGoogleData] = useState({ rating: 4.8, totalReviews: 23 });

  useEffect(() => {
    fetch('/api/reviews')
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => setGoogleData(data))
      .catch(() => {});
  }, []);

  const avgRating = googleData.rating.toFixed(1);
  const totalReviews = googleData.totalReviews;

  return (
    <section
      id="avis"
      className="py-16 sm:py-24 lg:py-32 bg-white"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-bold text-[#41DB23] mb-4 block">
            {t.testimonials.badge}
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-gray-900 mb-4">
            {t.testimonials.title}
          </h2>

          {/* Rating Summary */}
          <div className="inline-flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-full px-5 sm:px-6 py-2.5 sm:py-3 mt-4 shadow-sm">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 sm:w-5 sm:h-5 ${
                    i < Math.round(parseFloat(avgRating))
                      ? 'text-[#41DB23] fill-[#41DB23]'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-gray-900 font-bold text-base sm:text-lg">{avgRating}/5</span>
            <span className="text-gray-500 text-sm">— {totalReviews} {t.testimonials.reviewSuffix}</span>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {TESTIMONIALS.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>

        {/* Google Reviews Link */}
        <div className="mt-10 sm:mt-12 text-center">
          <a
            href="https://www.google.com/search?q=reco+pneus+avis"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#41DB23] hover:text-[#38C41E] font-medium text-sm sm:text-base transition-colors"
            data-testid="google-reviews-link"
          >
            {t.testimonials.viewAll}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
